/**
 * API base URL for the shared client.
 *
 * The SPA is static (built once, served from Cloudflare Pages), so the API
 * origin must be baked in at build time via a `VITE_*` env var — there is no
 * server of our own to read `process.env` at request time.
 * `VITE_API_URL` should point at the real Autar API, e.g.
 * `https://api.dev.autar.ai` in the dev deployment.
 *
 * No `/api` prefix: the API mounts routes at root (see CLAUDE.md "Server
 * route paths"). Callers pass paths like `/auth/login`, not `/api/auth/login`.
 */

// Matches the port the API is published on locally (SERVER_HTTP_PORT).
const DEFAULT_DEV_API_PORT = "3000";

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

export function getApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;

  // Nothing of ours sits in front of the production bundle, so an unset origin
  // there is a deliberate same-origin deployment rather than a misconfiguration.
  if (import.meta.env.PROD) {
    return configured ? trimTrailingSlash(configured) : "";
  }

  if (configured) return trimTrailingSlash(configured);

  // Dev must never fall back to same-origin: Vite answers unknown paths with
  // index.html and HTTP 200, so a relative API call comes back as HTML parsed
  // as JSON instead of a visible connection failure. Deriving from
  // window.location keeps tailnet and LAN visitors working too.
  const protocol = window.location.protocol.replace(":", "") || "http";
  const host = window.location.hostname || "localhost";
  const port = import.meta.env.VITE_SERVER_PORT || DEFAULT_DEV_API_PORT;
  return `${protocol}://${host}:${port}`;
}
